/**
 * Step 1.6 -- assembling a character out of modular parts.
 *
 * The whole point of the pipeline's canonical skeleton lands here: because every part
 * GLB carries the *same* skeleton with the *same* joint order, parts can be loaded
 * independently and then merged into a single skinned mesh sharing a single skeleton
 * and a single material. That is what makes many characters on screen affordable --
 * one draw call each, not one per slot.
 *
 * Merging is for the base body only. Anything that can change during a run -- a
 * helmet coming off, a shield going up -- stays a separate mesh parented to the
 * skeleton, because re-merging mid-combat is not acceptable. `SLOT_META[slot].merge`
 * in `@dungeon-builder/shared` says which is which.
 */
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Skeleton } from '@babylonjs/core/Bones/skeleton';
import type { Scene } from '@babylonjs/core/scene';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Material } from '@babylonjs/core/Materials/material';

import { loadSharedContainer } from './assetCache.ts';

// The glTF loader is not registered by default in an a-la-carte build.
import '@babylonjs/loaders/glTF/2.0';
import '@babylonjs/core/Materials/standardMaterial';

export interface HeroPart {
  /** Slot the part fills, for diagnostics. */
  slot: string;
  /** URL of the GLB holding the part. */
  url: string;
  /**
   * Which mesh to take from that file.
   *
   * A pack ships one GLB per *slot* carrying every enabled variant of it, so the
   * skeleton is stored once instead of once per variant. The file therefore holds
   * several meshes and the character wants exactly one; naming it here is how. Left
   * unset, every mesh in the file is used -- which is right for the one-part-per-file
   * layout the conversion cache uses.
   */
  meshName?: string;
  /**
   * Merge into the single body mesh, or keep separate so it can change mid-run.
   * Defaults to merging.
   */
  merge?: boolean;
}

export interface AssembleOptions {
  /**
   * Colour atlas to bind to the assembled character, for the untinted case.
   *
   * The part GLBs carry UVs and a named material but no texture: glTF forbids `..` in
   * image URIs and embedding the atlas would repeat it in every part. Ignored when
   * `material` is supplied.
   */
  atlasUrl?: string;
  /**
   * A ready-made material, normally from `createHeroTintMaterial`. Supplying it is
   * how a character gets its per-region tints while staying a single draw call.
   */
  material?: Material;
}

export interface AssembledHero {
  /** The merged body: one mesh, one material, one skeleton. */
  body: Mesh;
  /** Parts kept out of the merge, parented to the same skeleton. */
  attachments: AbstractMesh[];
  skeleton: Skeleton;
  /** Root of the skeleton's transform nodes; move this to move the character. */
  root: TransformNode;
  dispose(): void;
}

function skinnedMeshes(meshes: AbstractMesh[], meshName?: string): Mesh[] {
  const skinned = meshes.filter(
    (mesh): mesh is Mesh => mesh instanceof Mesh && (mesh.getTotalVertices?.() ?? 0) > 0,
  );
  if (!meshName) return skinned;
  const wanted = skinned.filter((mesh) => mesh.name === meshName);
  // The variants this character is not wearing were loaded with the file; they must
  // not linger in the scene or be swept into the merge.
  for (const mesh of skinned) if (!wanted.includes(mesh)) mesh.dispose();
  return wanted;
}

/**
 * Loads `parts` and returns one assembled character.
 *
 * The first part supplies the skeleton every other part is rebound to. That is safe
 * only because the pipeline writes every part against the canonical rig; a part
 * exported against its own partial bone list would silently skin to the wrong joints.
 */
export async function assembleHero(
  scene: Scene,
  parts: HeroPart[],
  options: AssembleOptions = {},
): Promise<AssembledHero> {
  if (parts.length === 0) throw new Error('assembleHero needs at least one part');

  // The parsed files are shared across every character in the scene (step 7.4); each
  // character takes a *copy* of the nodes it needs. `doNotInstantiate` forces real
  // clones rather than `InstancedMesh`es: the parts are about to be merged, and every
  // character animates its own skeleton, so there is nothing to instance.
  const containers = await Promise.all(parts.map((part) => loadSharedContainer(scene, part.url)));
  const copies = containers.map((container) =>
    container.instantiateModelsToScene((name) => name, false, { doNotInstantiate: true }),
  );

  const first = copies[0];
  if (!first) throw new Error('assembleHero: no containers loaded');
  const skeleton = first.skeletons[0];
  if (!skeleton) throw new Error(`${parts[0]?.url}: no skeleton in the part GLB`);

  // glTF is right-handed and Babylon is not, so the loader wraps every file in a
  // `__root__` node carrying that conversion. Nothing below it has a null parent,
  // and the conversion has to survive the merge -- so the character's movable root
  // is that node, found via the skeleton rather than by name.
  const rootBone = skeleton.bones[0]?.getTransformNode();
  const root =
    (rootBone?.parent as TransformNode | null) ??
    (first.rootNodes[0] as TransformNode | undefined) ??
    null;
  if (!root) throw new Error('assembleHero: the loaded skeleton has no root node');

  const toMerge: Mesh[] = [];
  const attachments: AbstractMesh[] = [];

  copies.forEach((copy, index) => {
    const part = parts[index];
    // An instantiated copy hands back root nodes, not a flat mesh list.
    const meshes = skinnedMeshes(
      copy.rootNodes.flatMap((node) => [node, ...node.getChildMeshes(false)]) as AbstractMesh[],
      part?.meshName,
    );

    for (const mesh of meshes) {
      // Detach from the conversion root *before* merging. `MergeMeshes` bakes each
      // source's world matrix into the merged vertices, so leaving the -X flip in
      // place would flip the positions while leaving `matricesIndices` untouched --
      // left-hand geometry weighted to right-hand bones, and arms that fly off the
      // body. Every part's local transform under its root is identity, so dropping
      // the parent makes all the world matrices identity and the merge neutral. The
      // flip is restored once, on the merged mesh, below.
      mesh.parent = null;
      mesh.skeleton = skeleton;
      if (part?.merge === false) attachments.push(mesh);
      else toMerge.push(mesh);
    }

    if (index === 0) return;

    // Everything this copy brought that the character does not need twice. Materials
    // and textures belong to the shared container, not to the copy, so they are left
    // alone -- the assembled body gets its own material below.
    for (const bones of copy.skeletons) bones.dispose();
    for (const node of copy.rootNodes) node.dispose(false, false);
  });

  if (toMerge.length === 0) {
    const named = parts.map((part) => part.meshName ?? part.slot).join(', ');
    throw new Error(`assembleHero: no mesh to merge (wanted ${named})`);
  }

  // `true` for disposeSource and for multiMultiMaterials; every part shares the one
  // hero atlas, so the merged mesh still ends up with a single material.
  const body = Mesh.MergeMeshes(toMerge, true, true, undefined, false, true);
  if (!body) throw new Error('assembleHero: MergeMeshes returned nothing');

  body.name = 'hero';
  // Re-adopt the conversion root, exactly once, now that the geometry is merged.
  // Bone matrices are computed in skeleton space (which excludes this node), so the
  // vertices must stay in that space too -- which is why the merge had to be neutral.
  body.parent = root;
  for (const mesh of attachments) mesh.parent = root;

  if (options.material) {
    body.material = options.material;
    for (const mesh of attachments) mesh.material = options.material;
  } else if (options.atlasUrl) {
    const material = new StandardMaterial('hero', scene);
    const atlas = new Texture(options.atlasUrl, scene, { noMipmap: false, invertY: false });
    material.diffuseTexture = atlas;
    // The POLYGON style is flat and unlit-looking; a specular highlight reads as a
    // rendering error on it.
    material.specularColor = new Color3(0, 0, 0);
    body.material = material;
    for (const mesh of attachments) mesh.material = material;
  }
  // MergeMeshes carries the vertex joint indices and weights across but not the
  // skeleton reference itself.
  body.skeleton = skeleton;
  for (const mesh of attachments) mesh.skeleton = skeleton;

  return {
    body,
    attachments,
    skeleton,
    root,
    dispose() {
      // A supplied material is the caller's to dispose -- it may be shared.
      if (!options.material) body.material?.dispose(true, true);
      body.dispose();
      for (const mesh of attachments) mesh.dispose();
      skeleton.dispose();
      root.dispose(false, true);
    },
  };
}
